<div class="users-container">

  <!-- ============================================================
       ✅ NO PERMISSION STATE — لو ملوش صلاحية Users.View
       ============================================================ -->
  @if (!canViewUsers()) {
    <div class="no-permission-state">
      <div class="no-permission-icon">
        <i class="fa-solid fa-lock"></i>
      </div>
      <h2 class="no-permission-title">Access Denied</h2>
      <p class="no-permission-message">
        You don't have permission to view users.
      </p>
      <p class="no-permission-hint">
        Please contact your administrator if you think this is a mistake.
      </p>
    </div>
  }

  <!-- ============================================================
       ✅ MAIN CONTENT — لو عنده صلاحية
       ============================================================ -->
  @if (canViewUsers()) {

    <!-- ========================= HEADER ========================= -->
    <div class="users-header">
      <div class="title-section">
        <div class="title-icon">
          <i class="fa-solid fa-users"></i>
        </div>
        <div>
          <h1 class="users-title">Users Management</h1>
          <span class="users-count">{{ totalItems() }} Users</span>
        </div>
      </div>

      <div class="header-actions">
        <div class="search-wrapper">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input type="text" placeholder="Search users..." class="search-input"
            [value]="searchTerm()" (input)="onSearch($event)" />
          @if (searchTerm()) {
            <button type="button" class="clear-search" (click)="clearSearch()">
              <i class="fa-solid fa-xmark"></i>
            </button>
          }
        </div>

        @if (canCreateUser()) {
          <button type="button" class="add-button" (click)="openCreateModal()">
            <i class="fa-solid fa-plus"></i>
            <span>Create User</span>
          </button>
        }
      </div>
    </div>

    <!-- ========================= FILTERS ========================= -->
    <div class="filters-card">
      <div class="filters-header">
        <div class="filters-title">
          <i class="fa-solid fa-filter"></i>
          <span>Filters</span>
        </div>
        @if (hasActiveFilters()) {
          <span class="active-filter-count">
            <i class="fa-solid fa-circle-check"></i> Active
          </span>
        }
      </div>

      <div class="users-filters">

        <div class="filter-group">
          <label><i class="fa-solid fa-user-tag"></i> User Type</label>
          <div class="select-wrapper">
            <select [value]="userTypeFilter()" (change)="onUserTypeChange($event)">
              <option value="">All Types</option>
              @for (t of userTypes(); track t) {
                <option [value]="t">{{ t }}</option>
              }
            </select>
            <i class="fa-solid fa-chevron-down"></i>
          </div>
        </div>

        <div class="filter-group">
          <label><i class="fa-solid fa-toggle-on"></i> Status</label>
          <div class="select-wrapper">
            <select [value]="statusFilter() ?? ''" (change)="onStatusChange($event)">
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <i class="fa-solid fa-chevron-down"></i>
          </div>
        </div>

        <div class="filter-group">
          <label><i class="fa-solid fa-ban"></i> Banned</label>
          <div class="select-wrapper">
            <select [value]="bannedFilter() ?? ''" (change)="onBannedChange($event)">
              <option value="">All</option>
              <option value="0">Not Banned</option>
              <option value="1">Banned</option>
            </select>
            <i class="fa-solid fa-chevron-down"></i>
          </div>
        </div>

        <button type="button" class="reset-filters-button"
          (click)="resetFilters()" [disabled]="!hasActiveFilters()">
          <i class="fa-solid fa-rotate-left"></i> Reset
        </button>

      </div>
    </div>

    <!-- ========================= TABLE ========================= -->
    <div class="users-table">
      <div class="table-header">
        <div class="header-cell">User</div>
        <div class="header-cell">Contact</div>
        <div class="header-cell">Type</div>
        <div class="header-cell">Roles</div>
        <div class="header-cell">Balance</div>
        <div class="header-cell">Status</div>
        <div class="header-cell">Actions</div>
      </div>

      <div class="table-body">
        @for (user of users(); track user.id) {
          <div class="user-row">

            <div class="cell user-cell">
              <div class="user-avatar">
                @if (user.avatar) {
                  <img [src]="user.avatar" [alt]="user.name" />
                } @else {
                  {{ getUserInitial(user) }}
                }
              </div>
              <div class="user-info">
                <div class="user-name">
                  {{ user.name }}
                  @if (user.banned === 1) {
                    <span class="banned-badge">
                      <i class="fa-solid fa-ban"></i> Banned
                    </span>
                  }
                </div>
                <div class="user-id">#{{ user.id }}</div>
              </div>
            </div>

            <div class="cell contact-cell">
              @if (user.email) {
                <div class="contact-item">
                  <i class="fa-solid fa-envelope"></i>
                  <span>{{ user.email }}</span>
                </div>
              }
              @if (user.phone) {
                <div class="contact-item">
                  <i class="fa-solid fa-phone"></i>
                  <span>{{ user.phone }}</span>
                </div>
              }
            </div>

            <div class="cell type-cell">
              <span class="type-badge" [class]="getUserTypeBadgeClass(user.user_type)">
                {{ user.user_type }}
              </span>
            </div>

            <div class="cell roles-cell">
              @if (user.roles.length > 0) {
                <div class="roles-list">
                  @for (role of user.roles; track role) {
                    <span class="role-chip">{{ role }}</span>
                  }
                </div>
              } @else {
                <span class="no-roles">No roles</span>
              }
            </div>

            <div class="cell balance-cell">
              <span class="balance-value">{{ user.balance | number:'1.2-2' }}</span>
            </div>

            <div class="cell status-cell">
              <div class="status-row">
                <label class="switch-container">
                  <input type="checkbox"
                    [checked]="user.status === 1"
                    [disabled]="!canBanUser()"
                    (change)="toggleStatus(user)"
                    class="toggle-switch" />
                  <span class="slider"></span>
                </label>
                <span class="status-label" [class.active]="user.status === 1">
                  {{ user.status === 1 ? 'Active' : 'Inactive' }}
                </span>
              </div>

              <div class="status-row">
                <label class="switch-container">
                  <input type="checkbox"
                    [checked]="user.banned === 1"
                    [disabled]="!canBanUser()"
                    (change)="toggleBan(user)"
                    class="toggle-switch toggle-ban" />
                  <span class="slider"></span>
                </label>
                <span class="status-label" [class.banned]="user.banned === 1">
                  {{ user.banned === 1 ? 'Banned' : 'Not Banned' }}
                </span>
              </div>
            </div>

            <div class="cell actions-cell">
              <div class="action-buttons">

                @if (canUpdateUser()) {
                  <button type="button" class="btn-action btn-edit"
                    (click)="openEditModal(user)" title="Edit">
                    <i class="fa-solid fa-pen"></i>
                  </button>
                }

                @if (canChangePassword()) {
                  <button type="button" class="btn-action btn-password"
                    (click)="openPasswordModal(user)" title="Change Password">
                    <i class="fa-solid fa-key"></i>
                  </button>
                }

                @if (canDeleteUser()) {
                  <button type="button" class="btn-action btn-delete"
                    (click)="confirmDelete(user)" title="Delete">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                }

                @if (!canUpdateUser() && !canChangePassword() && !canDeleteUser()) {
                  <span class="no-actions">—</span>
                }

              </div>
            </div>

          </div>
        } @empty {
          <div class="no-users">
            <div class="empty-icon"><i class="fa-solid fa-users"></i></div>
            <div class="empty-title">No Users Found</div>
            <div class="empty-description">Try changing your filters or create a new user.</div>
          </div>
        }
      </div>
    </div>

    <!-- ========================= PAGINATION ========================= -->
    @if (totalPages() > 0) {
      <div class="pagination-controls">
        <div class="pagination-info">
          Showing <strong>{{ startIndex() }}</strong> to <strong>{{ endIndex() }}</strong>
          of <strong>{{ totalItems() }}</strong> entries
        </div>

        <div class="pagination-buttons">
          <button type="button" (click)="previousPage()"
            [disabled]="currentPage() === 1 || isLoading()" class="pagination-btn">
            <i class="fa-solid fa-chevron-left"></i> Previous
          </button>

          <div class="page-numbers">
            @for (page of getPageNumbers(); track page) {
              <button type="button" (click)="goToPage(page)"
                [class.active]="page === currentPage()" [disabled]="isLoading()"
                class="page-btn">{{ page }}</button>
            }
          </div>

          <button type="button" (click)="nextPage()"
            [disabled]="currentPage() === totalPages() || isLoading()"
            class="pagination-btn">
            Next <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>

        <div class="items-per-page">
          <label>Items per page:</label>
          <select [value]="itemsPerPage()" (change)="onItemsPerPageChange($event)">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    }

    @if (isLoading()) {
      <div class="loading-indicator">
        <div class="spinner"></div>
        <span>Loading users...</span>
      </div>
    }
  }
</div>


<!-- ========================= CREATE / EDIT MODAL ========================= -->
@if (showUserModal()) {
  <div class="modal-overlay" (click)="closeUserModal()">
    <div class="modal user-modal" (click)="$event.stopPropagation()">

      <div class="modal-header">
        <div class="modal-icon" [class.edit]="isEditMode()">
          <i class="fa-solid" [class.fa-user-plus]="!isEditMode()" [class.fa-user-pen]="isEditMode()"></i>
        </div>
        <div>
          <h3 class="modal-title">{{ modalTitle() }}</h3>
          <p class="modal-subtitle">
            {{ isEditMode() ? 'Update user information' : 'Fill in the details to create a new user' }}
          </p>
        </div>
        <button type="button" class="modal-close" (click)="closeUserModal()" [disabled]="saving()">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <form class="modal-body" [formGroup]="userForm" (ngSubmit)="submitUser()">

        <div class="form-section-title">
          <i class="fa-solid fa-user"></i> Basic Information
        </div>

        <div class="form-row">

          <div class="form-group">
            <label class="form-label">
              Name <span class="required">*</span>
            </label>
            <input
              type="text"
              class="form-input"
              [class.input-error]="hasFieldError('name')"
              formControlName="name"
              placeholder="Full name"
              maxlength="255"
            />
            @if (hasFieldError('name')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('name') }}
              </span>
            }
            @if (!hasFieldError('name')) {
              <span class="form-hint">Required · Max 255 characters</span>
            }
          </div>

          <div class="form-group">
            <label class="form-label">
              User Type <span class="required">*</span>
            </label>
            <select
              class="form-input"
              [class.input-error]="hasFieldError('userType')"
              formControlName="userType">
              @for (t of userTypes(); track t) {
                <option [value]="t">{{ t }}</option>
              }
            </select>
            @if (hasFieldError('userType')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('userType') }}
              </span>
            }
            @if (!hasFieldError('userType')) {
              <span class="form-hint">Select from available types</span>
            }
          </div>

        </div>

        <div class="form-row">

          <div class="form-group">
            <label class="form-label">Email</label>
            <input
              type="email"
              class="form-input"
              [class.input-error]="hasFieldError('email')"
              formControlName="email"
              placeholder="email@example.com"
              maxlength="255"
            />
            @if (hasFieldError('email')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('email') }}
              </span>
            }
            @if (!hasFieldError('email')) {
              <span class="form-hint">Optional · Must be unique</span>
            }
          </div>

          <div class="form-group">
            <label class="form-label">Phone</label>
            <input
              type="text"
              class="form-input"
              [class.input-error]="hasFieldError('phone')"
              formControlName="phone"
              placeholder="+20..."
              maxlength="20"
            />
            @if (hasFieldError('phone')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('phone') }}
              </span>
            }
            @if (!hasFieldError('phone')) {
              <span class="form-hint">Optional · Max 20 characters</span>
            }
          </div>

        </div>

        @if (!isEditMode()) {
          <div class="form-row">

            <div class="form-group">
              <label class="form-label">
                Password <span class="required">*</span>
              </label>
              <input
                type="password"
                class="form-input"
                [class.input-error]="hasFieldError('password')"
                formControlName="password"
                placeholder="Minimum 6 characters"
              />
              @if (hasFieldError('password')) {
                <span class="form-error">
                  <i class="fa-solid fa-circle-exclamation"></i>
                  {{ getFieldError('password') }}
                </span>
              }
              @if (!hasFieldError('password')) {
                <span class="form-hint">Required · Min 6 characters</span>
              }
            </div>

            @if (canChangeBalance()) {
              <div class="form-group">
                <label class="form-label">Balance</label>
                <input
                  type="number"
                  class="form-input"
                  [class.input-error]="hasFieldError('balance')"
                  formControlName="balance"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
                @if (hasFieldError('balance')) {
                  <span class="form-error">
                    <i class="fa-solid fa-circle-exclamation"></i>
                    {{ getFieldError('balance') }}
                  </span>
                }
                @if (!hasFieldError('balance')) {
                  <span class="form-hint">Optional · Must be ≥ 0</span>
                }
              </div>
            }

          </div>
        }

        <div class="form-section-title">
          <i class="fa-solid fa-location-dot"></i> Address
        </div>

        <div class="form-row">

          <div class="form-group">
            <label class="form-label">Country</label>
            <input
              type="text"
              class="form-input"
              [class.input-error]="hasFieldError('country')"
              formControlName="country"
              maxlength="100"
            />
            @if (hasFieldError('country')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('country') }}
              </span>
            }
            @if (!hasFieldError('country')) {
              <span class="form-hint">Optional · Max 100 characters</span>
            }
          </div>

          <div class="form-group">
            <label class="form-label">State</label>
            <input
              type="text"
              class="form-input"
              [class.input-error]="hasFieldError('state')"
              formControlName="state"
              maxlength="100"
            />
            @if (hasFieldError('state')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('state') }}
              </span>
            }
            @if (!hasFieldError('state')) {
              <span class="form-hint">Optional · Max 100 characters</span>
            }
          </div>

        </div>

        <div class="form-row">

          <div class="form-group">
            <label class="form-label">City</label>
            <input
              type="text"
              class="form-input"
              [class.input-error]="hasFieldError('city')"
              formControlName="city"
              maxlength="100"
            />
            @if (hasFieldError('city')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('city') }}
              </span>
            }
            @if (!hasFieldError('city')) {
              <span class="form-hint">Optional · Max 100 characters</span>
            }
          </div>

          <div class="form-group">
            <label class="form-label">Postal Code</label>
            <input
              type="text"
              class="form-input"
              [class.input-error]="hasFieldError('postalCode')"
              formControlName="postalCode"
              maxlength="20"
            />
            @if (hasFieldError('postalCode')) {
              <span class="form-error">
                <i class="fa-solid fa-circle-exclamation"></i>
                {{ getFieldError('postalCode') }}
              </span>
            }
            @if (!hasFieldError('postalCode')) {
              <span class="form-hint">Optional · Max 20 characters</span>
            }
          </div>

        </div>

        <div class="form-group">
          <label class="form-label">Address</label>
          <textarea
            class="form-textarea"
            [class.input-error]="hasFieldError('address')"
            formControlName="address"
            rows="2"
            maxlength="500">
          </textarea>
          @if (hasFieldError('address')) {
            <span class="form-error">
              <i class="fa-solid fa-circle-exclamation"></i>
              {{ getFieldError('address') }}
            </span>
          }
          @if (!hasFieldError('address')) {
            <span class="form-hint">Optional · Max 500 characters</span>
          }
        </div>

        <div class="form-section-title">
          <i class="fa-solid fa-shield-halved"></i> Roles
        </div>

        @if (canAssignRole()) {
          <div class="roles-grid">
            @for (role of allRoles(); track role.id) {
              <label class="role-item" [class.selected]="isRoleSelected(role.id)">
                <input type="checkbox"
                  [checked]="isRoleSelected(role.id)"
                  (change)="toggleRole(role.id)" />
                <div class="role-info">
                  <span class="role-name">{{ role.name }}</span>
                  @if (role.description) {
                    <span class="role-desc">{{ role.description }}</span>
                  }
                </div>
              </label>
            } @empty {
              <p class="no-roles-hint">No roles available</p>
            }
          </div>
        } @else {
          <div class="no-permission-hint">
            <i class="fa-solid fa-lock"></i>
            You don't have permission to assign roles
          </div>
        }

      </form>

      <div class="modal-footer">
        <button type="button" class="modal-btn cancel" (click)="closeUserModal()" [disabled]="saving()">
          <i class="fa-solid fa-xmark"></i> Cancel
        </button>

        <button
          type="button"
          class="modal-btn confirm"
          (click)="submitUser()"
          [disabled]="saving() || userForm.invalid"
          [title]="userForm.invalid ? 'Please fill all required fields correctly' : ''">

          @if (saving()) {
            <i class="fa-solid fa-spinner fa-spin"></i> Saving...
          } @else {
            <i class="fa-solid" [class.fa-plus]="!isEditMode()" [class.fa-check]="isEditMode()"></i>
            {{ isEditMode() ? 'Save Changes' : 'Create User' }}
          }
        </button>
      </div>

    </div>
  </div>
}


<!-- ========================= DELETE MODAL ========================= -->
@if (showDeleteModal()) {
  <div class="modal-overlay" (click)="closeDeleteModal()">
    <div class="modal delete-modal" (click)="$event.stopPropagation()">
      <div class="delete-header">
        <div class="delete-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
        <h3 class="delete-title">Delete User?</h3>
        <p class="delete-subtitle">This action cannot be undone.</p>
      </div>

      @if (pendingDeleteUser(); as user) {
        <div class="delete-user-preview">
          <div class="delete-user-avatar">{{ getUserInitial(user) }}</div>
          <div class="delete-user-info">
            <strong>{{ user.name }}</strong>
            <span>{{ user.email || 'No email' }}</span>
          </div>
        </div>
      }

      <div class="modal-footer">
        <button type="button" class="modal-btn cancel" (click)="closeDeleteModal()" [disabled]="saving()">
          Cancel
        </button>
        <button type="button" class="modal-btn danger" (click)="deleteUser()" [disabled]="saving()">
          @if (saving()) {
            <i class="fa-solid fa-spinner fa-spin"></i> Deleting...
          } @else {
            <i class="fa-solid fa-trash"></i> Delete User
          }
        </button>
      </div>
    </div>
  </div>
}


<!-- ========================= PASSWORD MODAL ========================= -->
@if (showPasswordModal()) {
  <div class="modal-overlay" (click)="closePasswordModal()">
    <div class="modal password-modal" (click)="$event.stopPropagation()">

      <div class="modal-header">
        <div class="modal-icon password"><i class="fa-solid fa-key"></i></div>
        <div>
          <h3 class="modal-title">Change Password</h3>
          <p class="modal-subtitle">
            Set a new password for "{{ passwordUser()?.name }}"
          </p>
        </div>
        <button type="button" class="modal-close" (click)="closePasswordModal()" [disabled]="saving()">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <form class="modal-body" [formGroup]="passwordForm" (ngSubmit)="submitPassword()">
        <div class="form-group">
          <label class="form-label">New Password <span class="required">*</span></label>
          <input
            type="password"
            class="form-input"
            [class.input-error]="passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched"
            formControlName="newPassword"
            placeholder="Minimum 6 characters"
          />
          @if (passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched) {
            <span class="form-error">
              <i class="fa-solid fa-circle-exclamation"></i>
              @if (passwordForm.get('newPassword')?.errors?.['required']) {
                Password is required
              }
              @if (passwordForm.get('newPassword')?.errors?.['minlength']) {
                Minimum 6 characters
              }
            </span>
          }
          @if (!passwordForm.get('newPassword')?.invalid) {
            <span class="form-hint">Required · Min 6 characters</span>
          }
        </div>
      </form>

      <div class="modal-footer">
        <button type="button" class="modal-btn cancel" (click)="closePasswordModal()" [disabled]="saving()">
          Cancel
        </button>
        <button type="button" class="modal-btn confirm" (click)="submitPassword()"
          [disabled]="saving() || passwordForm.invalid">
          @if (saving()) {
            <i class="fa-solid fa-spinner fa-spin"></i> Saving...
          } @else {
            <i class="fa-solid fa-check"></i> Save Password
          }
        </button>
      </div>

    </div>
  </div>
}